// ════════════════════════════════════════════════════════════════════════════
// LpaTable — tabela primitiva com sort, paginação, sticky header, ARIA, empty state
// ════════════════════════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import LpaEmptyState from "./LpaEmptyState";

// ─── Tipos ────────────────────────────────────────────────────────────────

export type LpaCellAlign = "left" | "right" | "center";

export interface LpaColumn<Row> {
  /** Chave única e key acesso ao valor (suporta dot.path se necessário no acessor) */
  key: string;
  label: string;
  /** Largura CSS (ex: '30%', '120px', '1fr') */
  width?: string;
  align?: LpaCellAlign;
  /** Permite ordenar por essa coluna */
  sortable?: boolean;
  /** Acessor custom (default: row[key]) */
  accessor?: (row: Row) => unknown;
  /** Renderer custom (default: render bruto do acessor) */
  render?: (row: Row, index: number) => React.ReactNode;
  /** Para exportação CSV/JSON: formatador de string */
  format?: (val: unknown) => string;
  /** Tooltip no header */
  hint?: string;
}

export interface LpaTablePagination {
  pageSize: number;
  /** Página atual controlada externamente (opcional) */
  page?: number;
  /** Callback de mudança de página (controlled) */
  onPageChange?: (page: number) => void;
}

export interface LpaTableProps<Row> {
  /** Identificador para a11y (aria-label) */
  ariaLabel: string;
  columns: LpaColumn<Row>[];
  rows: Row[];
  /** ID único por linha — default usa index, sub-otimal */
  rowKey?: (row: Row, index: number) => string;
  pagination?: LpaTablePagination;
  /** Loading state — mostra skeleton no body */
  loading?: boolean;
  /** Componente custom para empty (default: LpaEmptyState no-data) */
  empty?: React.ReactNode;
  /** Click row */
  onRowClick?: (row: Row, index: number) => void;
  /** Sticky header (default true) */
  stickyHeader?: boolean;
  /** Sort inicial */
  initialSort?: { key: string; direction: "asc" | "desc" };
  /** Compact (linhas mais altas / fonte menor) */
  density?: "compact" | "comfortable";
  testId?: string;
  className?: string;
  style?: React.CSSProperties;
}

// ─── Sort util ────────────────────────────────────────────────────────────

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "pt-BR");
}

// ─── Component ────────────────────────────────────────────────────────────

export default function LpaTable<Row>({
  ariaLabel,
  columns,
  rows,
  rowKey,
  pagination,
  loading = false,
  empty,
  onRowClick,
  stickyHeader = true,
  initialSort,
  density = "comfortable",
  testId,
  className,
  style,
}: LpaTableProps<Row>): React.ReactElement {
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(
    initialSort ?? null
  );
  const [internalPage, setInternalPage] = useState(0);

  const page = pagination?.page ?? internalPage;
  const setPage = (p: number) => {
    if (pagination?.onPageChange) pagination.onPageChange(p);
    else setInternalPage(p);
  };

  // ── Sorting ──────────────────────────────────────────
  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const accessor =
      col.accessor ?? ((r: Row) => (r as unknown as Record<string, unknown>)[col.key]);
    const result = [...rows].sort((a, b) => compareValues(accessor(a), accessor(b)));
    if (sort.direction === "desc") result.reverse();
    return result;
  }, [rows, sort, columns]);

  // ── Pagination slice ─────────────────────────────────
  const pageSize = pagination?.pageSize ?? sortedRows.length;
  const totalPages = pagination ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const pageRows = useMemo(() => {
    if (!pagination) return sortedRows;
    const start = page * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize, pagination]);

  // ── Toggle sort ──────────────────────────────────────
  const handleSort = (key: string) => {
    if (!sort || sort.key !== key) {
      setSort({ key, direction: "asc" });
    } else if (sort.direction === "asc") {
      setSort({ key, direction: "desc" });
    } else {
      setSort(null);
    }
  };

  const cellPadding = density === "compact" ? "6px 10px" : "10px 12px";
  const fontSize = density === "compact" ? "var(--lpa-text-sm)" : "var(--lpa-text-md)";

  // ── Empty + loading guards ───────────────────────────
  if (!loading && rows.length === 0) {
    return (
      <div className={className} style={style}>
        {empty ?? <LpaEmptyState variant="no-data" />}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        background: "var(--lpa-card)",
        border: "1px solid var(--lpa-border)",
        borderRadius: "var(--lpa-radius-lg)",
        overflow: "hidden",
        ...style,
      }}
      data-testid={testId}
    >
      <div
        style={{
          overflowX: "auto",
          maxWidth: "100%",
        }}
      >
        <table
          aria-label={ariaLabel}
          role="table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize,
            fontFamily: "var(--lpa-font-sans)",
          }}
        >
          <thead>
            <tr>
              {columns.map((c) => {
                const isSorted = sort?.key === c.key;
                const ariaSort: "ascending" | "descending" | "none" | undefined = c.sortable
                  ? isSorted
                    ? sort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                  : undefined;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    aria-sort={ariaSort}
                    title={c.hint}
                    onClick={c.sortable ? () => handleSort(c.key) : undefined}
                    style={{
                      textAlign: c.align ?? "left",
                      padding: "12px 14px",
                      width: c.width,
                      fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--lpa-muted)",
                      borderBottom: "1px solid var(--lpa-border)",
                      background: "var(--lpa-surface)",
                      cursor: c.sortable ? "pointer" : "default",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      position: stickyHeader ? "sticky" : undefined,
                      top: stickyHeader ? 0 : undefined,
                      zIndex: stickyHeader ? 1 : undefined,
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {c.label}
                      {c.sortable && (
                        <span
                          aria-hidden="true"
                          style={{
                            fontSize: 9,
                            opacity: isSorted ? 1 : 0.35,
                            color: isSorted ? "var(--lpa-accent)" : "var(--lpa-muted)",
                          }}
                        >
                          {isSorted ? (sort.direction === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skeleton-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ padding: cellPadding }}>
                      <div className="lpa-skeleton" style={{ height: 12, width: "70%" }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  {empty ?? <LpaEmptyState variant="no-results" compact />}
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => {
                const key = rowKey?.(row, idx) ?? String(idx);
                return (
                  <tr
                    key={key}
                    className="lpa-table-row"
                    onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
                    style={{
                      cursor: onRowClick ? "pointer" : undefined,
                      transition: "background var(--lpa-tr-fast)",
                      background: idx % 2 === 0 ? "var(--lpa-row-1)" : "var(--lpa-row-2)",
                    }}
                  >
                    {columns.map((c) => {
                      const accessor =
                        c.accessor ??
                        ((r: Row) => (r as unknown as Record<string, unknown>)[c.key]);
                      const raw = accessor(row);
                      const content = c.render
                        ? c.render(row, idx)
                        : c.format
                          ? c.format(raw)
                          : raw == null
                            ? "—"
                            : String(raw);
                      return (
                        <td
                          key={c.key}
                          className="lpa-table-cell"
                          style={{
                            padding: "14px",
                            textAlign: c.align ?? "left",
                            fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
                            fontSize: 14,
                            color: "var(--lpa-text)",
                            borderBottom: "1px solid var(--lpa-border)",
                            verticalAlign: "middle",
                            whiteSpace: "nowrap",
                            opacity: 1,
                          }}
                        >
                          {content as React.ReactNode}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {pagination && totalPages > 1 && (
        <footer
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            borderTop: "1px solid var(--lpa-border)",
            background: "var(--lpa-surface)",
            fontFamily: "var(--lpa-font-mono)",
            fontSize: "var(--lpa-text-xs)",
            color: "var(--lpa-muted)",
          }}
        >
          <div>
            Página {page + 1} de {totalPages} · {sortedRows.length} registros
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              style={paginationBtnStyle(page === 0)}
            >
              ← Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              style={paginationBtnStyle(page >= totalPages - 1)}
            >
              Próxima →
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

function paginationBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "4px 10px",
    fontFamily: "var(--lpa-font-mono)",
    fontSize: "var(--lpa-text-2xs)",
    color: disabled ? "var(--lpa-muted)" : "var(--lpa-text)",
    background: "transparent",
    border: "1px solid var(--lpa-border)",
    borderRadius: "var(--lpa-radius-sm)",
    cursor: disabled ? "not-allowed" : "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    opacity: disabled ? 0.5 : 1,
  };
}
